// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson <hi@ben.gy>
// Additional terms under AGPL section 7 apply — see ADDITIONAL-TERMS.md.

/**
 * Model registry + the pure planning maths that decides how big a job we can
 * safely run. All side-effect free so it can be unit-tested without a worker.
 *
 * Swin2SR (a SwinIR-family super-resolution transformer) has fixed integer
 * scale factors, so the output is exactly `work × scale`. The transformers.js
 * `image-to-image` pipeline does NOT tile, so the whole image is held in memory
 * at once — we bound the *output* dimension and pre-scale oversized inputs.
 */

import type { Scale } from './types';

export interface ModelSpec {
  scale: Scale;
  /** Hugging Face model id (Xenova ONNX conversion). */
  id: string;
  /** Short label shown on the scale toggle. */
  label: string;
  /** One-line description of when to reach for it. */
  blurb: string;
  /** Approximate download size, MB, for the honest "downloads once" note. */
  sizeMB: number;
}

export const MODELS: Record<Scale, ModelSpec> = {
  2: {
    scale: 2,
    id: 'Xenova/swin2SR-classical-sr-x2-64',
    label: '2× · Sharp',
    blurb: 'Crisp doubling. Best for clean sources — logos, screenshots, line art.',
    sizeMB: 50,
  },
  4: {
    scale: 4,
    id: 'Xenova/swin2SR-realworld-sr-x4-64-bsrgan-psnr',
    label: '4× · Photo',
    blurb: 'Quadruples size and cleans up real-world photos, including compressed JPEGs.',
    sizeMB: 130,
  },
};

export function modelFor(scale: Scale): ModelSpec {
  return MODELS[scale];
}

/** The largest side we allow an upscaled result to reach, in pixels. */
export const MAX_OUTPUT_DIM = 4096;

export interface UpscalePlan {
  /** Dimensions actually fed to the model. */
  workWidth: number;
  workHeight: number;
  /** Final output dimensions (= work × scale). */
  outWidth: number;
  outHeight: number;
  /** True when the source was too big and had to be scaled down first. */
  downscaled: boolean;
}

/**
 * Decide the work + output size for an image of `w × h` at a given `scale`.
 * If `w × scale` (or height) would exceed `maxOutputDim`, the source is scaled
 * down proportionally so the result lands exactly on the bound.
 */
export function planUpscale(
  w: number,
  h: number,
  scale: Scale,
  maxOutputDim: number = MAX_OUTPUT_DIM,
): UpscalePlan {
  if (!Number.isFinite(w) || !Number.isFinite(h) || w < 1 || h < 1) {
    throw new Error('Image dimensions must be positive.');
  }
  const maxInputDim = Math.floor(maxOutputDim / scale);
  const longest = Math.max(w, h);

  let workWidth = Math.round(w);
  let workHeight = Math.round(h);
  let downscaled = false;

  if (longest > maxInputDim) {
    const k = maxInputDim / longest;
    workWidth = Math.max(1, Math.round(w * k));
    workHeight = Math.max(1, Math.round(h * k));
    downscaled = true;
  }

  return {
    workWidth,
    workHeight,
    outWidth: workWidth * scale,
    outHeight: workHeight * scale,
    downscaled,
  };
}

/** Rough guess at how many seconds inference will take, for a friendly hint. */
export function estimateSeconds(
  workPixels: number,
  scale: Scale,
  device: 'webgpu' | 'wasm',
): number {
  // Very rough throughput constants derived from Swin2SR runs; only used to set
  // expectations, never to gate anything.
  const outPixels = workPixels * scale * scale;
  const perMegapixel = device === 'webgpu' ? 1.4 : 9;
  return Math.max(1, (outPixels / 1_000_000) * perMegapixel);
}
